import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Headphones, Brain, Zap, Timer, Trophy, Palette,
  Play, ArrowRight, Check, Music, Waves,
  Sparkles, ChevronRight, Plane
} from 'lucide-react';
import AudioReactiveHero from './AudioReactiveHero';
import CoherenceExplainer from './CoherenceExplainer';
import SocialProofRotator from './SocialProofRotator';
import CoherencePreviewPlayer from './CoherencePreviewPlayer';

const FEATURES = [
  { icon: Headphones, title: 'Binaural & 8D Audio', desc: 'Five beat types including binaural, isochronic, and immersive 8D spatial audio that surrounds your mind.', color: '#8B5CF6' },
  { icon: Brain, title: 'AI Session Creator', desc: 'Tell the AI your goal — focus, sleep, creativity — and it designs a personalized brainwave session for you.', color: '#06B6D4' },
  { icon: Timer, title: 'Visual Timeline Editor', desc: 'Drag frequency and volume nodes on an interactive timeline. Design complex brainwave journeys visually.', color: '#10B981' },
  { icon: Music, title: '27 Science-backed Presets', desc: 'From deep sleep to peak focus. Every protocol tagged by evidence tier with peer-reviewed citations — no medical claims, just honest research.', color: '#F59E0B' },
  { icon: Trophy, title: 'Achievements & Stats', desc: 'Track your progress with 12 achievements, session streaks, and detailed listening analytics.', color: '#EF4444' },
  { icon: Palette, title: 'Custom Themes', desc: 'Build your own color theme with gradient and accent pickers. Make NeuroWave truly yours.', color: '#EC4899' },
];

const STEPS = [
  { num: '01', title: 'Choose a Preset', desc: 'Pick from 27 science-backed presets or let AI design one for your specific goal.', icon: Sparkles },
  { num: '02', title: 'Customize Your Journey', desc: 'Fine-tune frequencies, volume curves, and effects on the interactive timeline editor.', icon: Waves },
  { num: '03', title: 'Press Play & Relax', desc: 'Put on headphones and let neural entrainment guide your brain into the desired state.', icon: Play },
];

const PREMIUM_FEATURES = [
  'AI Session Creator',
  '8D Spatial Audio',
  'Export Sessions as WAV',
  'Unlimited Custom Presets',
  'Full Analytics Dashboard',
  'Custom Theme Builder',
  'Priority Support',
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0118] text-white overflow-hidden" data-testid="landing-page">
      {/* Ambient background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-purple-600/10 blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-cyan-600/8 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] rounded-full bg-blue-600/6 blur-[100px]" />
      </div>

      {/* Nav */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollY > 50 ? 'bg-[#0a0118]/80 backdrop-blur-xl border-b border-white/5' : ''}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
              <Headphones className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">NeuroWave One</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/science')}
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/5 hidden sm:inline-flex"
              data-testid="nav-science"
            >
              Science
            </Button>
            <Button
              onClick={() => navigate('/app')}
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/5"
              data-testid="nav-open-app"
            >
              Open App
            </Button>
            <Button
              onClick={() => navigate('/app')}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-5"
              data-testid="nav-get-started"
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6" data-testid="hero-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-8">
            <Zap className="w-3.5 h-3.5 text-purple-400" />
            <span className="text-purple-300 text-sm">Brainwave Entrainment Technology</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
            <span className="bg-gradient-to-r from-white via-purple-100 to-white bg-clip-text text-transparent">
              Train Your Brain
            </span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              With Sound
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-white/50 max-w-2xl mx-auto mb-10 leading-relaxed">
            Professional brainwave entrainment with binaural beats, 8D spatial audio,
            AI-powered sessions, and an interactive timeline editor.
            Sharpen focus. Deepen sleep. Unlock creativity.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              onClick={() => navigate('/app')}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white text-lg px-8 py-6 rounded-xl font-semibold"
              data-testid="hero-cta"
            >
              Start Free Session <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <button
              type="button"
              onClick={() => navigate('/app?coherence=1')}
              data-testid="hero-coherence-cta"
              className="group inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-rose-300/40 bg-gradient-to-r from-rose-500/10 via-pink-500/15 to-rose-500/10 hover:border-rose-300/70 hover:from-rose-500/20 hover:via-pink-500/25 hover:to-rose-500/20 transition-all text-white shadow-sm"
            >
              <span className="text-base leading-none">💓</span>
              <span className="font-medium">Try Coherence Mode</span>
              <span className="text-xs text-white/60 group-hover:text-white/80 hidden sm:inline">
                · 2-min preview
              </span>
            </button>
            <span className="text-white/30 text-sm">No account required</span>
          </div>

          {/* "Works offline" credibility badge — leans on the iter-44/45
              service-worker + localStorage offline architecture. Surfacing
              this hits a real audience pain point ("offline meditation app"
              is a high-intent search query) and differentiates from the
              ~all of competitors that require a constant network. Subtle
              by design — it's a credibility cue, not a primary CTA. */}
          <div className="flex justify-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-300/20 bg-cyan-500/[0.04] text-cyan-200/70 text-xs font-medium tracking-wide"
              data-testid="offline-capable-badge"
            >
              <Plane className="w-3.5 h-3.5" />
              <span>Works offline · Start a free session</span>
            </div>
          </div>

          {/* Expandable "What's Coherence Mode?" explainer — surfaces the
              physiological-realism stack (real sample, HRV, RSA, 5.5bpm
              breath) in clinical vocabulary that wellness customers
              recognise from HeartMath/Polar/Whoop. Collapsed by default so
              the hero stays scannable. */}
          <CoherenceExplainer />

          {/* Rotating one-line social proof. Pulls visitors over the
              "is this real?" hump right before they hit the audio-reactive
              canvas + Features section. Default copy is use-case-framed
              (NOT attributed to fictitious people) — replace with real
              attributed testimonials before paid launch. See the file's
              top-of-file comment for swap instructions. */}
          <SocialProofRotator />

          {/* Inline 30-second audio preview. One-click → real heartbeat
              + alpha binaural pad in the visitor's ears, no signup, no
              navigation. Highest-leverage friction-reducer on the page —
              every CTA above this point routes elsewhere first. */}
          <CoherencePreviewPlayer />

          {/* Animated, mouse-reactive brainwave visualization (canvas) */}
          <AudioReactiveHero barCount={72} height={112} />
          <p className="text-white/25 text-xs mt-3">
            <span className="hidden sm:inline">Hover the waves — your brain on sound.</span>
            <span className="sm:hidden">Live brainwave visualization</span>
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-24 px-6" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Everything You Need for
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent"> Neural Training</span>
            </h2>
            <p className="text-white/40 text-lg max-w-2xl mx-auto">
              A complete toolkit for brainwave entrainment, from beginner-friendly presets to advanced frequency engineering.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className="group relative bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-300"
                  data-testid={`feature-card-${i}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${f.color}18` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: f.color }} />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{f.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-24 px-6" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Three Steps to a
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent"> Better Mind</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="relative text-center" data-testid={`step-${i}`}>
                  <div className="text-6xl font-bold text-white/[0.04] absolute top-0 left-1/2 -translate-x-1/2">{s.num}</div>
                  <div className="relative pt-8">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 border border-purple-500/20 flex items-center justify-center mx-auto mb-5">
                      <Icon className="w-7 h-7 text-purple-400" />
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-2">{s.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                  {i < 2 && (
                    <ChevronRight className="hidden md:block absolute top-1/2 -right-6 w-5 h-5 text-white/20" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative py-24 px-6" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Simple
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent"> Pricing</span>
            </h2>
            <p className="text-white/40 text-lg">Start free. Upgrade once. Premium forever.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Free */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8">
              <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider mb-2">Free</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white">$0</span>
                <span className="text-white/30 text-sm">forever</span>
              </div>
              <p className="text-white/30 text-sm mb-6">Great for exploring brainwave states</p>
              <div className="space-y-3 mb-8">
                {['Basic presets', 'Binaural & monaural beats', 'Session history', 'Achievements tracking', '3 color themes'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-white/30 flex-shrink-0" />
                    <span className="text-white/50 text-sm">{f}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => navigate('/app')}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5 py-5"
                data-testid="pricing-free-btn"
              >
                Get Started
              </Button>
            </div>

            {/* Premium */}
            <div className="relative bg-gradient-to-br from-purple-600/10 to-cyan-600/10 border border-purple-500/30 rounded-2xl p-8">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                BEST VALUE
              </div>
              <h3 className="text-purple-300 text-sm font-medium uppercase tracking-wider mb-2">Premium</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white">$9.99</span>
                <span className="text-white/30 text-sm">lifetime</span>
              </div>
              <p className="text-white/30 text-sm mb-6">One payment. All features. Forever.</p>
              <div className="space-y-3 mb-8">
                {PREMIUM_FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    <span className="text-white/70 text-sm">{f}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => navigate('/app')}
                className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold py-5"
                data-testid="pricing-premium-btn"
              >
                Get Premium — $9.99
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Early supporter — honest positioning instead of fake testimonials */}
      <section className="relative py-24 px-6" data-testid="early-supporter-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span className="text-cyan-200 text-sm">New — built openly</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Be an
            <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent"> early supporter</span>
          </h2>
          <p className="text-white/50 text-lg leading-relaxed mb-8">
            NeuroWave just launched. We haven't collected a wall of glowing testimonials yet — and we're not going to fake them. Instead, here's what we can promise the people who try this early:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left mb-10">
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-5">
              <div className="text-purple-300 text-xs font-semibold uppercase tracking-wider mb-2">Honest science</div>
              <p className="text-white/60 text-sm leading-relaxed">Every protocol tagged by evidence tier, every claim cited. No chakra realignment. No DNA repair. Just research.</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-5">
              <div className="text-cyan-300 text-xs font-semibold uppercase tracking-wider mb-2">Lifetime price locked</div>
              <p className="text-white/60 text-sm leading-relaxed">Pay $9.99 once and every future feature is yours forever. No subscriptions, no upsells, no price hikes for early users.</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-5">
              <div className="text-purple-300 text-xs font-semibold uppercase tracking-wider mb-2">Your feedback shapes it</div>
              <p className="text-white/60 text-sm leading-relaxed">Reply to any email we send. We read every message and ship based on what early users actually want — not a roadmap someone printed in 2019.</p>
            </div>
          </div>
          <p className="text-white/30 text-xs">
            Want to be featured here with a real quote later? Try it for a week, then tell us how it went.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Train Your Brain?
          </h2>
          <p className="text-white/40 text-lg mb-8">
            Start your first session in 30 seconds — no account needed to explore. Sharpen focus, deepen sleep, unlock creativity.
          </p>
          <Button
            onClick={() => navigate('/app')}
            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white text-lg px-10 py-6 rounded-xl font-semibold"
            data-testid="final-cta-btn"
          >
            Launch NeuroWave <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
              <Headphones className="w-3 h-3 text-white" />
            </div>
            <span className="text-white/40 text-sm">NeuroWave One</span>
          </div>
          <div className="text-white/20 text-sm">
            Secure payments by Stripe. Built with science.
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
